/**
 * Cookie Preferences Modal
 * Detailed cookie consent management with granular controls
 * GDPR/CCPA/PDPA compliant
 */

import { useState, useEffect } from 'react';
import { Dialog } from '@headlessui/react';
import { X, Shield, BarChart3, Target, Check } from 'lucide-react';
import { Link } from 'react-router-dom';
import { getConsent, setConsent, revokeConsent } from '../../utils/consentManager';
import { TRACKING_SERVICES } from '../../types/consent';

interface CookiePreferencesModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave?: () => void;
}

export default function CookiePreferencesModal({
  isOpen,
  onClose,
  onSave
}: CookiePreferencesModalProps) {
  const [preferences, setPreferences] = useState({
    essential: true,
    analytics: false,
    marketing: false
  });

  // Load current preferences on mount
  useEffect(() => {
    if (isOpen) {
      const current = getConsent();
      setPreferences({
        essential: current.essential,
        analytics: current.analytics,
        marketing: current.marketing
      });
    }
  }, [isOpen]);

  const handleSave = () => {
    setConsent({
      analytics: preferences.analytics,
      marketing: preferences.marketing
    });

    if (onSave) {
      onSave();
    }
    onClose();
  };

  const handleWithdrawAll = () => {
    revokeConsent();
    setPreferences({
      essential: true,
      analytics: false,
      marketing: false
    });

    if (onSave) {
      onSave();
    }
    onClose();
  };

  const categories = [
    {
      id: 'essential',
      name: 'Essential Cookies',
      icon: Shield,
      description: 'Required for the website to function properly. These cannot be disabled.',
      enabled: preferences.essential,
      locked: true,
      services: [
        'Site navigation and functionality',
        'User authentication (JWT tokens)',
        'Form submissions',
        'Session management'
      ],
      dataCollected: ['Session tokens', 'Authentication tokens', 'Form inputs'],
      retention: 'Session or as needed for functionality'
    },
    {
      id: 'analytics',
      name: 'Analytics Cookies',
      icon: BarChart3,
      description: 'Help us understand how visitors interact with our website by collecting and reporting information anonymously.',
      enabled: preferences.analytics,
      locked: false,
      services: TRACKING_SERVICES.filter(s => s.category === 'analytics'),
      toggle: () => setPreferences(prev => ({ ...prev, analytics: !prev.analytics }))
    },
    {
      id: 'marketing',
      name: 'Marketing Cookies',
      icon: Target,
      description: 'Used to track visitors across websites to deliver relevant advertising and measure campaign effectiveness.',
      enabled: preferences.marketing,
      locked: false,
      services: TRACKING_SERVICES.filter(s => s.category === 'marketing'),
      toggle: () => setPreferences(prev => ({ ...prev, marketing: !prev.marketing }))
    }
  ];

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/30" aria-hidden="true" />

      {/* Full-screen container */}
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="mx-auto max-w-4xl w-full bg-white rounded-xl shadow-2xl max-h-[90vh] flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div>
              <Dialog.Title className="text-2xl font-bold text-gray-900">
                Cookie Preferences
              </Dialog.Title>
              <p className="text-sm text-gray-600 mt-1">
                Manage your cookie and tracking preferences
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors p-2 rounded-lg hover:bg-gray-100"
              aria-label="Close dialog"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Content - Scrollable */}
          <div className="flex-1 overflow-y-auto p-6">
            <div className="space-y-6">
              {/* Introduction */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-900">
                  <strong>Your Privacy Matters:</strong> We respect your right to privacy
                  and comply with GDPR, CCPA, PDPA, and other privacy regulations.
                  You can customize which cookies to accept and change your preferences at any time.
                </p>
              </div>

              {/* Cookie Categories */}
              {categories.map((category) => (
                <div
                  key={category.id}
                  className="border border-gray-200 rounded-lg p-5 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4 flex-1">
                      <div className={`p-3 rounded-lg ${
                        category.enabled
                          ? 'bg-primary/10 text-primary'
                          : 'bg-gray-100 text-gray-400'
                      }`}>
                        <category.icon className="w-6 h-6" />
                      </div>

                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">
                          {category.name}
                        </h3>
                        <p className="text-sm text-gray-600 mb-4">
                          {category.description}
                        </p>

                        {/* Services List */}
                        {Array.isArray(category.services) && category.services.length > 0 && (
                          <div className="space-y-3">
                            {category.id === 'essential' ? (
                              // Simple list for essential
                              <ul className="text-sm text-gray-700 space-y-1">
                                {(category.services as string[]).map((service, idx) => (
                                  <li key={idx} className="flex items-center gap-2">
                                    <Check className="w-4 h-4 text-green-600" />
                                    {service}
                                  </li>
                                ))}
                              </ul>
                            ) : (
                              // Detailed list for analytics/marketing
                              category.services.map((service: any, idx) => (
                                <div key={idx} className="bg-gray-50 rounded p-3 text-sm">
                                  <p className="font-medium text-gray-900">{service.name}</p>
                                  <p className="text-gray-600 mt-1">{service.description}</p>
                                  <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
                                    <div>
                                      <span className="font-medium">Purpose:</span> {service.purpose}
                                    </div>
                                    <div>
                                      <span className="font-medium">Retention:</span> {service.retention}
                                    </div>
                                    <div>
                                      <span className="font-medium">Provider:</span> {service.provider}
                                    </div>
                                    {service.thirdPartySharing && (
                                      <div className="text-orange-600">
                                        <span className="font-medium">Third-party sharing</span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              ))
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Toggle Switch */}
                    <div className="ml-4">
                      {category.locked ? (
                        <div className="text-sm text-gray-500 font-medium">Always Active</div>
                      ) : (
                        <button
                          type="button"
                          onClick={category.toggle}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 ${
                            category.enabled ? 'bg-primary' : 'bg-gray-300'
                          }`}
                          aria-pressed={category.enabled}
                          aria-label={`Toggle ${category.name}`}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                              category.enabled ? 'translate-x-6' : 'translate-x-1'
                            }`}
                          />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}

              {/* Additional Information */}
              <div className="bg-gray-50 rounded-lg p-4 text-sm text-gray-600">
                <p className="mb-2">
                  <strong>Learn more:</strong> For detailed information about how we collect
                  and use your data, please read our{' '}
                  <Link to="/privacy-policy" className="text-primary hover:underline">
                    Privacy Policy
                  </Link>
                  {' '}and{' '}
                  <Link to="/cookie-policy" className="text-primary hover:underline">
                    Cookie Policy
                  </Link>.
                </p>
                <p>
                  <strong>Contact us:</strong> For privacy-related questions or to exercise
                  your rights, email us at{' '}
                  <a href="mailto:contact@emploskills.com" className="text-primary hover:underline">
                    contact@emploskills.com
                  </a>
                </p>
              </div>
            </div>
          </div>

          {/* Footer - Actions */}
          <div className="border-t border-gray-200 p-6 bg-gray-50">
            <div className="flex flex-col sm:flex-row gap-3 justify-between">
              <button
                onClick={handleWithdrawAll}
                className="px-6 py-2.5 text-sm font-medium text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
              >
                Withdraw All Consent
              </button>

              <div className="flex gap-3">
                <button
                  onClick={onClose}
                  className="px-6 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  className="px-6 py-2.5 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary-dark transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 shadow-lg"
                >
                  Save Preferences
                </button>
              </div>
            </div>
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
}
